import { z } from 'zod';

import { MAX_FILE_SIZE_MB, validateFileFormat, validateFileSize } from '@/lib/validation';

// const MAX_FILE_SIZE_MB = 25;

// const validateFileSize = (file: File | string): boolean => {
//   if (file instanceof File) {
//     const sizeInMB = file.size / (1024 * 1024);

//     return sizeInMB <= MAX_FILE_SIZE_MB;
//   }

//   return true;
// };

// Блок 1: Дані документа, що посвідчує особу
const personalIdentificationSchema = z
  .object({
    newIDCard: z
      .string()
      .min(9, 'Номер ID-картки повинен містити 9 цифр')
      .max(9, 'Номер ID-картки повинен містити 9 цифр')
      .optional()
      .or(z.literal('')),
    oldPassport: z.object({
      number: z
        .string()
        .min(6, 'Номер повинен містити 6 цифр')
        .max(6, 'Номер повинен містити не більше 6 цифр')
        .optional()
        .or(z.literal('')),
      series: z
        .string()
        .min(2, 'Серія містить дві літери')
        .max(2, 'Серія паспорту повинна містити не більше двох літер')
        .optional()
        .or(z.literal('')),
    }),
  })
  .superRefine((data, ctx) => {
    const { number: oldPassportNumber, series: oldPassportSeries } = data.oldPassport;

    const newIDCardFilled = data.newIDCard;

    // Перевіряємо, що заповнено хоча б одне з полів (старий паспорт або ID-картка)
    if (!oldPassportSeries && !oldPassportNumber && !newIDCardFilled) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Необхідно заповнити дані паспорту або ID-картки',
        // path: ['personalIdentification'],
      });
    }

    // Перевіряємо, що обидва поля старого паспорту заповнені, якщо заповнене хоча б одне з них
    if ((oldPassportSeries && !oldPassportNumber) || (!oldPassportSeries && oldPassportNumber)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Необхідно заповнити і серію, і номер паспорту',
        // path: ['personalIdentification'],
      });
    }

    // Перевіряємо, що обидва поля не заповнені одночасно (старий паспорт і ID-картка)
    if ((oldPassportSeries || oldPassportNumber) && newIDCardFilled) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Можна заповнити або паспорт, або ID-картку, але не обидва',
        // path: ['personalIdentification'],
      });
    }
  });

// Блок 2: Правовстановлюючий документ на землю
const ownershipDocumentSchema = z
  .array(
    z
      .custom<File | string>()
      .refine((file) => validateFileFormat(file), {
        message:
          'Допустимі формати файлів: .xlsx, .xls, .doc, .docx, .ppt, .pptx, .txt, .pdf, зображення.',
      })
      .refine((file) => validateFileSize(file), {
        message: `Розмір файлу не повинен перевищувати ${MAX_FILE_SIZE_MB} МБ`,
      }),
  )
  // .min(1, 'Необхідно завантажити правовстановлюючий документ')
  .max(1, 'Максимально 1 документ')
  .optional();

// Блок 3: Дані ідентифікаційного номера платника податків
const tinSchema = z
  .string()
  .min(10, 'ІПН повинен містити 10 цифр')
  .max(10, 'ІПН повинен містити максимально 10 цифр')
  .optional()
  .or(z.literal(''));

// Блок 4: Додаткові документи
const additionalDocumentsSchema = z
  .array(
    z
      .custom<File | string>()
      .refine((file) => validateFileFormat(file), {
        message:
          'Невірний формат файлу. Допустимі формати файлів: .xlsx, .xls, .doc, .docx, .ppt, .pptx, .txt, .pdf, зображення.',
      })
      .refine((file) => validateFileSize(file), {
        message: `Розмір файлу не повинен перевищувати ${MAX_FILE_SIZE_MB} МБ`,
      }),
  )
  .optional();

// Загальна схема форми
export const passportFormSchema = z
  .object({
    documents: additionalDocumentsSchema,
    ownershipDocument: ownershipDocumentSchema,
    personalIdentification: personalIdentificationSchema,
    tin: tinSchema,
  })
  .superRefine((data, ctx) => {
    const { tin } = data;

    // Перевірка, що ІПН (tin) обов'язковий
    if (!tin) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ІПН є обов'язковим для заповнення",
        path: ['tin'],
      });
    }
  });

export type FormValues = z.infer<typeof passportFormSchema>;
